import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, X, Search } from "lucide-react";

export interface MultiSelectOption {
  value: string;
  label: string;
  hint?: string;
}

interface SearchableMultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
}

export default function SearchableMultiSelect({
  options,
  value,
  onChange,
  placeholder = "Pilih...",
  emptyText = "Tidak ada data",
  disabled = false,
}: SearchableMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      // Focus search input when opening
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const selectedOptions = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.hint && o.hint.toLowerCase().includes(q)),
    );
  }, [options, query]);

  function toggle(val: string) {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  }

  function removeOne(val: string, e: React.MouseEvent) {
    e.stopPropagation();
    onChange(value.filter((v) => v !== val));
  }

  function clearAll(e: React.MouseEvent) {
    e.stopPropagation();
    onChange([]);
  }

  return (
    <div ref={wrapperRef} className="relative">
      {/* Trigger / chip area */}
      <div
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`min-h-[42px] w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm transition-colors ${
          disabled ? "cursor-not-allowed bg-gray-50" : "cursor-pointer hover:border-gray-400"
        } ${open ? "border-blue-500 ring-1 ring-blue-500" : ""}`}
      >
        <div className="flex flex-wrap items-center gap-1">
          {selectedOptions.length === 0 && (
            <span className="px-1 text-gray-400">{placeholder}</span>
          )}
          {selectedOptions.map((o) => (
            <span
              key={o.value}
              className="flex items-center gap-1 rounded-md bg-blue-100 px-2 py-0.5 text-xs text-blue-800"
            >
              {o.label}
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => removeOne(o.value, e)}
                  className="hover:text-blue-600"
                  aria-label={`Hapus ${o.label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
          <div className="ml-auto flex items-center gap-1 pl-1">
            {selectedOptions.length > 0 && !disabled && (
              <button
                type="button"
                onClick={clearAll}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Kosongkan"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari..."
              className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">{emptyText}</li>
            )}
            {filtered.map((o) => {
              const selected = value.includes(o.value);
              return (
                <li
                  key={o.value}
                  onClick={() => toggle(o.value)}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-blue-50 ${
                    selected ? "bg-blue-50" : ""
                  }`}
                >
                  <div
                    className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                      selected ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300"
                    }`}
                  >
                    {selected && <Check className="h-3 w-3" />}
                  </div>
                  <span className="flex-1 text-gray-700">
                    {o.hint && (
                      <span className="font-mono text-xs text-gray-500">{o.hint} </span>
                    )}
                    {o.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
